/**
 * Shared link helpers for TiptapEditor and NotionEditor (/ → Link, toolbar Link).
 */

const SUPPORTED_PROTOCOL_PATTERN = /^(https?:\/\/|mailto:|tel:)/i;

function isValidHostname(hostname) {
  if (!hostname) {
    return false;
  }

  const host = hostname.split(":")[0];
  if (host === "localhost") {
    return true;
  }

  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return true;
  }

  return host.includes(".") && /^[\w.-]+$/.test(host);
}

function isValidMailtoHref(value) {
  return /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+/i.test(value);
}

function isValidTelHref(value) {
  return /^tel:[+\d\s().-]+$/i.test(value.trim());
}

function isValidHttpHref(value) {
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return false;
    }

    return isValidHostname(parsed.hostname);
  } catch {
    return false;
  }
}

/**
 * Normalize and validate a user-entered link.
 * Bare domains (example.com, test.co) get https:// prepended when valid.
 * @param {string} url
 * @returns {string|null}
 */
export function normalizeLinkHref(url) {
  const trimmed = (url || "").trim();
  if (!trimmed) {
    return null;
  }

  if (/^mailto:/i.test(trimmed)) {
    return isValidMailtoHref(trimmed) ? trimmed : null;
  }

  if (/^tel:/i.test(trimmed)) {
    return isValidTelHref(trimmed) ? trimmed : null;
  }

  const withProtocol = SUPPORTED_PROTOCOL_PATTERN.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  return isValidHttpHref(withProtocol) ? withProtocol : null;
}

/**
 * Href that a readonly reader may open: only http(s), mailto and tel (the same
 * schemes normalizeLinkHref accepts). Anything else (javascript:, data:, bare text) is null.
 * @param {string|null|undefined} href
 * @returns {string|null}
 */
export function getOpenableLinkHref(href) {
  const trimmed = (href || "").trim();
  return SUPPORTED_PROTOCOL_PATTERN.test(trimmed) ? trimmed : null;
}

/**
 * Readonly click on a link: swallow the click (no edit popup, no same-tab navigation)
 * and open the href in a new tab. Unsafe schemes are swallowed and not opened.
 * stopImmediatePropagation keeps the second capture handler on the same editor from opening it twice.
 * @param {Event} event
 * @param {HTMLAnchorElement} anchor
 */
export function openLinkAnchorReadonly(event, anchor) {
  event.preventDefault();
  event.stopImmediatePropagation();

  const href = getOpenableLinkHref(anchor?.getAttribute("href"));
  if (href) {
    window.open(href, "_blank", "noopener,noreferrer");
  }
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * @returns {DOMRect | { left: number, top: number, right: number, bottom: number, width: number, height: number }}
 */
function createClientRect(x, y, width, height) {
  if (typeof DOMRect !== "undefined") {
    return new DOMRect(x, y, width, height);
  }

  return {
    x,
    y,
    width,
    height,
    left: x,
    top: y,
    right: x + width,
    bottom: y + height,
  };
}

/**
 * Build a DOMRect for tippy positioning from the current editor selection.
 * @param {import('@tiptap/core').Editor} editor
 */
export function getLinkReferenceRect(editor) {
  const view = editor?.view;
  if (!view || typeof view.coordsAtPos !== "function") {
    return createClientRect(0, 0, 1, 1);
  }

  const { from, to } = editor.state.selection;
  const start = view.coordsAtPos(from);
  const end = view.coordsAtPos(to);

  return createClientRect(
    start.left,
    Math.min(start.top, end.top),
    Math.max(end.right - start.left, 1),
    Math.max(start.bottom, end.bottom) - Math.min(start.top, end.top)
  );
}

/**
 * Apply or insert a hyperlink at the current selection.
 * Empty selection → insert linked URL text. Non-empty → wrap selection.
 * @param {import('@tiptap/core').Editor} editor
 * @param {string} href
 * @param {{ displayText?: string }} [options]
 * @returns {boolean}
 */
export function applyEditorLink(editor, href, options = {}) {
  const normalizedHref = normalizeLinkHref(href);
  if (!editor || !normalizedHref) {
    return false;
  }

  const { empty } = editor.state.selection;
  if (empty) {
    const displayText = options.displayText || normalizedHref;
    editor
      .chain()
      .focus()
      .insertContent([
        {
          type: "text",
          text: displayText,
          marks: [{ type: "link", attrs: { href: normalizedHref } }],
        },
        { type: "text", text: " " },
      ])
      .run();
    return true;
  }

  editor.chain().focus().extendMarkRange("link").setLink({ href: normalizedHref }).run();
  return true;
}

export function unsetEditorLink(editor) {
  if (!editor) {
    return false;
  }

  editor.chain().focus().extendMarkRange("link").unsetLink().run();
  return true;
}

/**
 * @param {EventTarget|null} target
 * @param {HTMLElement|null} editorDom
 * @returns {HTMLAnchorElement|null}
 */
export function findEditorLinkAnchor(target, editorDom) {
  if (!(target instanceof Element) || !editorDom) {
    return null;
  }

  const anchor = target.closest("a.editor-link, a.tiptap-link, a[href]");
  if (!(anchor instanceof HTMLAnchorElement) || !editorDom.contains(anchor)) {
    return null;
  }

  return anchor;
}

/**
 * Move selection onto a clicked hyperlink so the link editor can edit it.
 * @param {import('@tiptap/core').Editor} editor
 * @param {HTMLAnchorElement} anchor
 * @returns {boolean}
 */
export function selectLinkAtAnchor(editor, anchor) {
  if (!editor?.view || !anchor) {
    return false;
  }

  try {
    const pos = editor.view.posAtDOM(anchor, 0);
    if (typeof pos !== "number") {
      return false;
    }

    editor.chain().focus().setTextSelection(pos).extendMarkRange("link").run();
    return editor.isActive("link");
  } catch {
    return false;
  }
}
